import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import {
    fetchTransferHistorys as OnfetchTransferHistorys,
    exportTransferHistoryCSV
} from 'slices/supervisor/stock/transferHistory/thunk'
import WarehouseTableContainer from 'Common/WarehouseTableContainer'
import { fetchDamages } from 'slices/Warehouse/ProjectWarehouse/Damage/thunk'
import { fetchDamagesItems } from 'slices/Warehouse/ProjectWarehouse/DamageItem/thunk'
import { fetchWarehouseDropdown } from 'slices/Dropdowns/Warehouse/WarehouseDropdown/thunk'

const DamageItemsDetailsListings: React.FC = () => {
    document.title = 'Damage Items List'
    const dispatch = useDispatch<any>();
    const { state } = useLocation();


    const selectTransferHistorys = createSelector(
        (state: any) => state.damageItems,
        pkg => ({
            TransferHistorys: pkg.list,
            rowCount: pkg.rowCount,
            loading: pkg.loading,
            error: pkg.error
        })
    )

    const { TransferHistorys, rowCount, loading, error } = useSelector(
        selectTransferHistorys
    )

    const { warehouseDropdownList, warehouseDropdownLoading, selectedWarehouse } =
        useSelector(
            createSelector(
                (state: any) => state.warehouseDropdownReducer,
                (data) => ({
                    warehouseDropdownList: data.list,
                    warehouseDropdownLoading: data.loading,
                    warehouseDropdownError: data.error,
                    selectedWarehouse: data.selected,
                })
            )
        );

    const user = useSelector((state: any) => state.Login.user)

    const [pageIndex, setPageIndex] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [data, setData] = useState([])
    const [colValue, setColValue] = useState<any>()
    const [totalRecords, setTotalRecords] = useState(rowCount)
    const navigate = useNavigate()
    const { id } = useParams()


    const [editTransferHistory, setEditTransferHistory] = useState<boolean>(false)
    const [columnFilters, setColumnFilters] = useState<any>([])
    const [filterValue, setFilterValue] = useState<any[]>([])
    const [edit, setEdit] = useState<any>()
    const [sorting, setSorting] = useState<SortingState>([])
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const sortField = sorting[0]?.id ?? ''
    const sortDirection = sorting[0]?.desc ? 'desc' : 'asc'
    const location = useLocation()
    const currentPath = location.pathname

    useEffect(() => {
        if (!state) return;
        if (!selectedWarehouse?.warehouse?.warehouse_id) return;
        dispatch(
            fetchDamagesItems({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortField ? sortDirection : "",
                search,
                filters: filterValue,
                state,
                warehouse_id: selectedWarehouse?.warehouse?.warehouse_id,
            })
        );
    }, [dispatch, id, pageIndex, pageSize, sorting, search, filterValue, selectedWarehouse?.warehouse?.warehouse_id,
        warehouseDropdownList,]);


    useEffect(() => {
        setPageIndex(0)
    }, [sorting, pageSize, columnFilters])

    useEffect(() => {
        setData(TransferHistorys)
    }, [TransferHistorys])
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            // setSearch(searchInput);
            setPageIndex(0)
        }, 500)

        return () => clearTimeout(delayDebounce)
    }, [searchInput])

    useEffect(() => {
        dispatch(fetchWarehouseDropdown());
    }, [dispatch]);



    // Handle edit action
    const handleEdit = (item: any) => {
        setEditTransferHistory(!editTransferHistory)
        setEdit({
            id: item.id,
            TransferHistoryName: item.TransferHistory_name,
            state: item.state_name,
            status: item.is_active,
            createdBy: item.created_by,
            updatedBy: item.updated_by,
            createDate: item.created_at,
            updatedDate: item.updated_at
        })
    }

    const handleExport = async () => {
        try {
            const { blob, filename } = await exportTransferHistoryCSV()

            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Export failed', error)
            alert('Export failed')
        }
    }

    const columns = useMemo(
        () => [
            {
                header: 'Item Name',
                accessorKey: 'item_name',
                enableColumnFilter: false,
                enableSorting: false,
                filterType: 'dropdown' as const,
                filterFetchUrl: '/Damage/dropdown/TransferHistory_name'
            },
            {
                header: 'Unit',
                accessorKey: 'unit',
                enableColumnFilter: false,
                enableSorting: false,
                filterType: 'dropdown' as const,
                filterFetchUrl: '/Damage/dropdown/state_name'
            },
            {
                header: 'Serial No',
                accessorKey: 'serial_no',
                enableColumnFilter: false,
                enableSorting: false,
                filterType: 'dropdown' as const,
                filterFetchUrl: '/Damage/dropdown/state_name'
            },
            {
                header: "Status",
                accessorKey: "qc_pass",
                enableColumnFilter: false,
                enableSorting: false,
                cell: ({ row }: { row: any }) => {
                    const qc_pass = row.original.qc_pass;

                    const status = qc_pass === "fail" ? "Damaged" : "Robust";
                    const backgroundColor = !qc_pass ? "#0CBA91" : "#fb4a4aff";

                    return (
                        <span
                            className="badge d-inline-flex align-items-center gap-2 text-uppercase"
                            style={{
                                borderRadius: "10px",
                                backgroundColor,
                                color: "white",
                                fontWeight: 500,
                            }}
                        >
                            <span
                                style={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: "50%",
                                    backgroundColor: "#fff",
                                    display: "inline-block",
                                }}
                            />
                            {status}
                        </span>
                    );
                },
            },



            {
                header: 'Action',
                enableColumnFilter: false,
                enableSorting: false,
                cell: ({ row }: { row: any }) => {
                    const dispatch_done_id = row.original.dispatch_done_id

                    return (
                        <ul className='d-flex gap-2 list-unstyled mb-0'>
                            <li>
                                <button
                                    className='btn btn-sm'
                                    style={{ backgroundColor: '#3F7DDA', color: '#FFF' }}
                                >
                                    View OR
                                </button>
                            </li>
                        </ul>
                    )
                }
            }
        ],
        [dispatch, TransferHistorys]
    )
    function handleChange(e: any): void {
        setSearchInput(e.target.value)
    }

    return (
        <Fragment>
            <div className='page-content'>
                <Container fluid>
                    <Row id='invoiceList'>
                        <Col lg={12}>
                            <Card>
                                <Card.Header>
                                    <Row className='align-items-center g-2 py-3'>
                                        <Col lg={3} className='me-auto d-flex align-items-center'>
                                            <i
                                                className='bi bi-arrow-left me-2'
                                                style={{ fontSize: '1.25rem', cursor: 'pointer' }}
                                                onClick={() => navigate('/warehouse-damage')}
                                            ></i>
                                            <h6 className='card-title mb-0'>Items Details Item</h6>
                                        </Col>

                                        <Col className='col-md-auto'></Col>
                                    </Row>
                                    <Row>
                                        <div className='d-flex gap-3 mt-3'>
                                            <Link
                                                to='/warehouse-daily-log'
                                                style={{ textDecoration: 'none' }}
                                            ></Link>
                                        </div>
                                    </Row>
                                </Card.Header>
                                <Card.Body className='mt-3'>
                                    {loading ? (
                                        <div className='text-center py-4'>
                                            <h5>Loading...</h5>
                                        </div>
                                    ) : error ? (
                                        <div className=''>
                                            {/* <h5>Error: {error}</h5> */}
                                            <WarehouseTableContainer
                                                columns={columns}
                                                // columns={[]}
                                                data={data}
                                                totalRecords={rowCount}
                                                pageIndex={pageIndex}
                                                pageSize={pageSize}
                                                onPageChange={setPageIndex}
                                                onPageSizeChange={setPageSize}
                                                columnFilters={columnFilters}
                                                setColumnFilters={setColumnFilters}
                                                colValue={setFilterValue}
                                                sorting={sorting}
                                                onSortingChange={setSorting}
                                                isBordered={false}
                                                isPagination={true}
                                                isGlobalFilter={false}
                                                SearchPlaceholder='Search TransferHistorys...'
                                                manualSorting={true}
                                            />
                                        </div>
                                    ) : (
                                        <WarehouseTableContainer
                                            columns={columns}
                                            // columns={[]}
                                            data={data}
                                            totalRecords={rowCount}
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            onPageChange={setPageIndex}
                                            onPageSizeChange={setPageSize}
                                            columnFilters={columnFilters}
                                            setColumnFilters={setColumnFilters}
                                            colValue={setFilterValue}
                                            sorting={sorting}
                                            onSortingChange={setSorting}
                                            isBordered={false}
                                            isPagination={true}
                                            isGlobalFilter={false}
                                            SearchPlaceholder='Search TransferHistorys...'
                                            manualSorting={true}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </Fragment>
    )
}

export default DamageItemsDetailsListings
